import {
	AlertBar,
	ColorPicker,
	FormItem,
	RadioButtonGroup,
	RadioButtonGroupItem,
	ValidationError,
	DimensionRecommendationKind,
	useAlertDialog
} from "client/Components";
import { IObjectInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	BrandingImageConstraints,
	BrandingLogoModel,
	BrandingSettingsSaveModel,
	ContrastColorStyle,
	LiveTileHeaderStyle,
	WorkspaceHeaderStyle
} from "client/Api/Branding/branding";
import { FileInput } from "./FileInput";
import styles from "./ThemesTab.module.less";
import { ColorPickerWithPrimaryColorContrastRequirements } from "./ColorPickerWithPrimaryColorContrastRequirements";

export type LightThemeProps = {
	hasCustomBackgroundImage: boolean;
	onHasCustomBackgroundImageChange(newValue: boolean): void;
	attachable: IObjectInputAttachable<BrandingSettingsSaveModel>;
	workspaceLogoId: string | null;
	publicPageLogoId: string | null;
	workspaceBackgroundId: string | null;
	homeLogoId: string | null;
	logoConstraints: BrandingImageConstraints;
	backgroundConstraints: BrandingImageConstraints;
};

export const LightTheme = (props: LightThemeProps): ReactElement => {
	const {
		attachable,
		backgroundConstraints,
		logoConstraints,
		homeLogoId,
		publicPageLogoId,
		workspaceBackgroundId,
		workspaceLogoId,
		hasCustomBackgroundImage,
		onHasCustomBackgroundImageChange
	} = props;

	const [isContrastColorStyleAlertHidden, setIsContrastColorStyleAlertHidden] =
		useState<boolean>(false);

	const [alertDialog, openAlertDialog] = useAlertDialog();

	const showPrimaryColorContrastInfoDialogHandler = useCallback(
		(): void => {
			openAlertDialog({
				title: localize("_PrimaryColorForLightTheme"),
				children: localize("_WeRecommendAPrimaryColorThatMeetsTheAccessibilityStandards"),
				showCloseButton: true,
				trackingId: "lt-primary-color-info-dialog"
			});
		},
		[openAlertDialog]
	);

	const showLinkColorContrastInfoDialogHandler = useCallback(
		(): void => {
			openAlertDialog({
				title: localize("_TextLinkColorForLightTheme"),
				children: localize("_WeRecommendATextLinkColorThatMeetsTheAccessibilityStandards"),
				showCloseButton: true,
				trackingId: "lt-text-link-color-info-dialog"
			});
		},
		[openAlertDialog]
	);

	return (
		<>
			<section className={styles.section}>
				<h3 className={styles.header}>{localize("_ThemeLight")}</h3>
				<FormItem label={localize("_HeaderColor")}>
					<ColorPicker
						{...attachable.for("headerColor").attachToColorPicker()}
						menuTitle={localize("_HeaderColor")}
					/>
					<ValidationError {...attachable.for("headerColor").attachToValidationError()} />
				</FormItem>
				<FormItem
					label={localize("_HeaderStyling")}
					isForGroup
				>
					<RadioButtonGroup
						{...attachable
							.for("workspaceHeaderStyle")
							.attachToRadioButtonGroup()
						}
						options={headerStyleOptions}
					/>
					<ValidationError
						{...attachable.for("workspaceHeaderStyle")
							.attachToValidationError()
						}
					/>
				</FormItem>
				<ColorPickerWithPrimaryColorContrastRequirements
					label={localize("_PrimaryColor")}
					attachable={attachable.for("primaryColor")}
					onShowInfoDialog={showPrimaryColorContrastInfoDialogHandler}
				/>
				<ColorPickerWithPrimaryColorContrastRequirements
					label={localize("_TextLinkColor")}
					attachable={attachable.for("textLinkColor")}
					onShowInfoDialog={showLinkColorContrastInfoDialogHandler}
				/>
				<FormItem
					label={localize("_LiveTileStyling")}
					isForGroup
				>
					<RadioButtonGroup
						{...attachable
							.for("liveTileHeaderStyle")
							.attachToRadioButtonGroup()
						}
						options={liveTileStyleOptions}
					/>
					<ValidationError
						{...attachable.for("liveTileHeaderStyle")
							.attachToValidationError()
						}
					/>
				</FormItem>
				<FormItem
					label={localize("_StylingForTextAndIconsOnAColoredBackground")}
					isForGroup
				>
					<RadioButtonGroup
						{...attachable
							.for("contrastColorStyle")
							.attachToRadioButtonGroup()
						}
						options={contrastColorStyleOptions}
					/>
					<ValidationError
						{...attachable.for("contrastColorStyle")
							.attachToValidationError()
						}
					/>
					{!isContrastColorStyleAlertHidden
						&& attachable.for("contrastColorStyle")
							.value === ContrastColorStyle.Light && (
								<AlertBar
									kind="warning"
									onDismiss={() => setIsContrastColorStyleAlertHidden(true)}
									isInline
								>
									{localize("_WhiteTextAndIconsDoNotAlwaysHaveAClearContrastAgainst ColoredBackgroundWarning")}
								</AlertBar>
							)
					}
				</FormItem>
				<WorkspaceBackgroundInput
					attachable={attachable.for("workspaceBackground")}
					workspaceBackgroundId={workspaceBackgroundId}
					logoConstraints={backgroundConstraints}
					hasCustomImage={hasCustomBackgroundImage}
					onHasCustomImageChange={onHasCustomBackgroundImageChange}
				/>
				<FileInput
					label={localize("_WorkspaceLogo")}
					attachable={attachable.for("workspaceLogo")}
					originalImageId={workspaceLogoId}
					supportedImageTypes={logoConstraints.supportedImageTypes}
					supportedFileDetails={logoConstraints}
					dimensionRecomendation={{
						kind: DimensionRecommendationKind.Height,
						height: 50
					}}
					supportedExtensions={[localize("_FileExtensionJpg"), localize("_FileExtensionPng")]}
				/>
				<FileInput
					label={localize("_PublicPageLogo")}
					attachable={attachable.for("publicPageLogo")}
					originalImageId={publicPageLogoId}
					supportedImageTypes={logoConstraints.supportedImageTypes}
					supportedFileDetails={logoConstraints}
					dimensionRecomendation={{
						kind: DimensionRecommendationKind.Height,
						height: 50
					}}
					supportedExtensions={[localize("_FileExtensionJpg"), localize("_FileExtensionPng")]}
				/>
				<FileInput
					label={localize("_HomeLogo")}
					attachable={attachable.for("homeLogo")}
					originalImageId={homeLogoId}
					supportedImageTypes={logoConstraints.supportedImageTypes}
					supportedFileDetails={logoConstraints}
					dimensionRecomendation={{
						kind: DimensionRecommendationKind.Height,
						height: 32
					}}
					supportedExtensions={[localize("_FileExtensionJpg"), localize("_FileExtensionPng")]}
				/>
			</section>
			{alertDialog}
		</>
	);
};
LightTheme.displayName = "LightTheme";

export type WorkspaceBackgroundInputProps = {
	hasCustomImage: boolean;
	onHasCustomImageChange(newValue: boolean): void;
	attachable: IValueInputAttachable<BrandingLogoModel>;
	workspaceBackgroundId: string | null;
	logoConstraints: BrandingImageConstraints;
};

export const WorkspaceBackgroundInput = (props: WorkspaceBackgroundInputProps): ReactElement => {
	const {
		hasCustomImage,
		onHasCustomImageChange,
		attachable,
		workspaceBackgroundId,
		logoConstraints
	} = props;

	return (
		<>
			<FormItem
				label={localize("_WorkspaceBackgroundStyling")}
				isForGroup
			>
				<RadioButtonGroup
					value={hasCustomImage}
					onChange={onHasCustomImageChange}
					options={workspaceBackgroundStylingOptions}
					trackingId="light-theme-workspace-background-styling"
				/>
				{!hasCustomImage && (
					<ValidationError {...attachable.attachToValidationError()} />
				)}
			</FormItem>
			{hasCustomImage && (
				<FileInput
					label={localize("_WorkspaceBackgroundImage")}
					attachable={attachable}
					originalImageId={workspaceBackgroundId}
					supportedImageTypes={logoConstraints.supportedImageTypes}
					canBeCleared={false}
					supportedFileDetails={logoConstraints}
					dimensionRecomendation={{
						kind: DimensionRecommendationKind.Dimension,
						height: 1080,
						width: 1920,
						shouldProposeHigherDimension: true
					}}
					supportedExtensions={[
						localize("_FileExtensionSvg"),
						localize("_FileExtensionJpg"),
						localize("_FileExtensionPng")
					]}
				/>
			)}
		</>
	);
};
WorkspaceBackgroundInput.displayName = "WorkspaceBackgroundInput";


const headerStyleOptions: readonly RadioButtonGroupItem<WorkspaceHeaderStyle>[] = [
	{
		text: localize("_WhiteHeaderWithColoredProfileButton"),
		value: WorkspaceHeaderStyle.whiteBackground
	},
	{
		text: localize("_ColoredHeaderWithWhiteProfileButton"),
		value: WorkspaceHeaderStyle.coloredBackground
	}
];

const liveTileStyleOptions: readonly RadioButtonGroupItem<LiveTileHeaderStyle>[] = [
	{
		text: localize("_WhiteHeaderWithColoredText"),
		value: LiveTileHeaderStyle.coloredText
	},
	{
		text: localize("_ColoredHeaderWithWhiteText"),
		value: LiveTileHeaderStyle.coloredBackground
	}
];

const contrastColorStyleOptions: readonly RadioButtonGroupItem<ContrastColorStyle>[] = [
	{
		text: localize("_UseOptimalColorsForTextAndIcons"),
		value: ContrastColorStyle.Optimal
	},
	{
		text: localize("_AlwaysUseWhiteForTextAndIcons"),
		value: ContrastColorStyle.Light
	}
];

const workspaceBackgroundStylingOptions: readonly RadioButtonGroupItem<boolean>[] = [
	{
		text: localize("_GreyBackgroundColor"),
		value: false,
		valueTrackingId: "grey-background-color"
	},
	{
		text: localize("_CustomImage"),
		value: true,
		valueTrackingId: "custom-image"
	}
];
